import React, { useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useInvoicePermissions } from "../../../hooks/purchaseInvoices/useInvoicePermissions";
import { useResponsive } from "../../../hooks/purchaseInvoices/useResponsive";
import useInvoiceTable from '../../../hooks/purchaseInvoices/invoiceManagement/useInvoiceTable';
import InvoiceDesktopView from "./InvoiceDesktopView";
import InvoiceMobileView from "./InvoiceMobileView";
import InvoiceDetailsPopup from "./InvoiceDetailsPopup";
import EditInvoiceDialog from './EditInvoiceDialog';
import ConfirmationDialog from "../../../pages/purchaseInvoices/supplierInvoiceFiles/ConfirmationDialog";
import { StyledContainer, StyledCard } from "../../../styles/purchaseInvoices/pages/commonTableStyles";

function InvoiceTable({
    onOpenDrawer,
    onOpenManualDrawer,
    highlightedInvoiceId,
    onHighlightComplete
}) {
    const { canEdit, canDelete, canUpload } = useInvoicePermissions();
    const { isDesktop } = useResponsive();

    // Local state for actions menu
    const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
    const [activeInvoiceId, setActiveInvoiceId] = useState(null);

    // Custom hook - contains all business logic
    const hookData = useInvoiceTable({
        highlightedInvoiceId,
        onHighlightComplete
    });

    const {
        openEdit,
        setOpenEdit,
        editInvoice,
        openInvoicePopup,
        setOpenInvoicePopup,
        selectedInvoiceForPopup,
        approvalPackageForPopup,
        openDelete,
        setOpenDelete,
        deleteMutation,
        updateMutation,
        fileAsBase64,
        isLoading,
        isLoadingBackground,
        isLoadingFile,
        selectedFileName,
        setSelectedFileName,
        allocationKeyModalOpen,
        setAllocationKeyModalOpen,
        allocationKeys,
        isLoadingAllocationKeys,
        allocationKeysError,
        currentInvoiceYear,
        onSaveNotes,
        onApproveInvoice,
        onRejectInvoice,
        handleSaveEdit,
        handleReprocessAttention,
        initiateApproval,
        completeApprovalWithAllocationKey,
        isReprocessing,
        getAccountantDisplayName,
        accountantNamesCache
    } = hookData;

    const handleActionsClick = (event, invoiceId) => {
        event.stopPropagation();
        setActionMenuAnchorEl(event.currentTarget);
        setActiveInvoiceId(invoiceId);
    };

    const handleActionsClose = () => {
        setActionMenuAnchorEl(null);
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
                <Typography variant="h6" color="textSecondary" sx={{ ml: 2 }}>
                    טוען נתונים...
                </Typography>
            </Box>
        );
    }

    return (
        <StyledContainer>
            {isLoadingBackground && (
                <Box display="flex" alignItems="center" sx={{ px: 2, py: 0.5, bgcolor: 'action.hover', borderRadius: 1, mb: 1 }}>
                    <CircularProgress size={14} sx={{ mr: 1 }} />
                    <Typography variant="caption" color="textSecondary">
                        טוען חשבוניות נוספות...
                    </Typography>
                </Box>
            )}
            <StyledCard>
                {isDesktop ? (
                    <InvoiceDesktopView
                        hookData={hookData}
                        actionMenuAnchorEl={actionMenuAnchorEl}
                        activeInvoiceId={activeInvoiceId}
                        onActionsClick={handleActionsClick}
                        onActionsClose={handleActionsClose}
                        onOpenDrawer={onOpenDrawer}
                        onOpenManualDrawer={onOpenManualDrawer}
                        canEdit={canEdit}
                        canDelete={canDelete}
                        canUpload={canUpload}
                    />
                ) : (
                    <InvoiceMobileView
                        hookData={hookData}
                        actionMenuAnchorEl={actionMenuAnchorEl}
                        activeInvoiceId={activeInvoiceId}
                        onActionsClick={handleActionsClick}
                        onActionsClose={handleActionsClose}
                        onOpenDrawer={onOpenDrawer}
                        onOpenManualDrawer={onOpenManualDrawer}
                        canEdit={canEdit}
                        canDelete={canDelete}
                        canUpload={canUpload}
                    />
                )}
            </StyledCard>

            <InvoiceDetailsPopup
                open={openInvoicePopup}
                onClose={() => setOpenInvoicePopup(false)}
                invoice={selectedInvoiceForPopup}
                approvalPackageData={approvalPackageForPopup}
                onSaveNotes={onSaveNotes}
                onApproveInvoice={onApproveInvoice}
                onRejectInvoice={onRejectInvoice}
                fileAsBase64={fileAsBase64}
                isLoadingFile={isLoadingFile}
                initiateApproval={initiateApproval}
                allocationKeyModalOpen={allocationKeyModalOpen}
                setAllocationKeyModalOpen={setAllocationKeyModalOpen}
                allocationKeys={allocationKeys}
                isLoadingAllocationKeys={isLoadingAllocationKeys}
                allocationKeysError={allocationKeysError}
                currentInvoiceYear={currentInvoiceYear}
                completeApprovalWithAllocationKey={completeApprovalWithAllocationKey}
                getAccountantDisplayName={getAccountantDisplayName}
                accountantNamesCache={accountantNamesCache}
            />

            <EditInvoiceDialog
                open={openEdit}
                onClose={() => setOpenEdit(false)}
                invoice={editInvoice}
                onSave={handleSaveEdit}
                onReprocessAttention={handleReprocessAttention}
                isSaving={updateMutation.isPending || isReprocessing}
            />

            <ConfirmationDialog
                selectedFileName={selectedFileName}
                openDelete={openDelete}
                onClose={() => setOpenDelete(false)}
                onDelete={() => {
                    deleteMutation.mutate(selectedFileName, {
                        onSuccess: () => {
                            setSelectedFileName(null);
                        }
                    });
                }}
                isPending={deleteMutation.isPending}
            />
        </StyledContainer>
    );
}

export default InvoiceTable;